/**
 * Shared lightbox modal used by single-post.php, single-resource.php, and
 * single-special_project.php. Keep this the single source of truth so all
 * three pages behave identically — do not fork per-page copies of this logic.
 */
export function initPostMediaModal() {
  const modal = document.getElementById('post-image-modal');
  if (!modal) return;

  const overlay = modal.querySelector('.post-image-modal__overlay');
  const content = modal.querySelector('.post-image-modal__content');
  const closeBtn = modal.querySelector('.post-image-modal__close');
  const prevBtn = modal.querySelector('.post-image-modal__nav--prev');
  const nextBtn = modal.querySelector('.post-image-modal__nav--next');
  const modalImage = modal.querySelector('.post-image-modal__image');
  const modalVideo = modal.querySelector('.post-image-modal__video');
  const modalCaption = modal.querySelector('.post-image-modal__caption');

  const triggers = Array.prototype.slice.call(document.querySelectorAll('.post-media-trigger'));
  if (!triggers.length) return;

  let currentIndex = -1;
  let lastFocusedElement = null;

  function readPayload(trigger) {
    const type = trigger.getAttribute('data-media-type');
    if (type === 'image') {
      return {
        type: 'image',
        src: trigger.getAttribute('data-full-src') || '',
        alt: trigger.getAttribute('data-alt') || '',
        caption: trigger.getAttribute('data-caption') || '',
      };
    }
    if (type === 'video_file') {
      return {
        type: 'video_file',
        videoSrc: trigger.getAttribute('data-video-src') || '',
      };
    }
    return {
      type: 'video',
      embedHtml: trigger.getAttribute('data-embed-html') || '',
    };
  }

  // Injects autoplay into an oEmbed iframe's src so the video starts as soon
  // as the modal opens, rather than requiring an extra click inside the embed.
  function withAutoplay(embedHtml) {
    return embedHtml.replace(
      /(<iframe[^>]+src=["'])([^"']+)(["'])/i,
      function (match, pre, src, post) {
        const sep = src.indexOf('?') !== -1 ? '&' : '?';
        return pre + src + sep + 'autoplay=1' + post;
      }
    );
  }

  function render(payload) {
    if (payload.type === 'image') {
      modalImage.src = payload.src;
      modalImage.alt = payload.alt;
      modalImage.style.display = '';
      modalVideo.innerHTML = '';
      modalVideo.setAttribute('aria-hidden', 'true');
    } else if (payload.type === 'video_file') {
      modalImage.src = '';
      modalImage.alt = '';
      modalImage.style.display = 'none';
      modalVideo.innerHTML = payload.videoSrc
        ? '<video controls autoplay playsinline style="width:100%;max-height:80vh"><source src="' + payload.videoSrc + '"></video>'
        : '';
      modalVideo.setAttribute('aria-hidden', 'false');
      // The `autoplay` attribute alone is unreliable across browsers (Safari
      // in particular requires play() to be called directly within the user
      // gesture's call stack, not just be present on newly inserted markup).
      // Call it explicitly, mirroring the resource-loop-nmtc video modal.
      var videoEl = modalVideo.querySelector('video');
      if (videoEl) {
        videoEl.play().catch(function () {});
      }
    } else {
      modalImage.src = '';
      modalImage.alt = '';
      modalImage.style.display = 'none';
      modalVideo.innerHTML = payload.embedHtml ? withAutoplay(payload.embedHtml) : '';
      modalVideo.setAttribute('aria-hidden', 'false');
    }

    const caption = payload.caption || '';
    modalCaption.textContent = caption;
    modalCaption.style.display = caption ? '' : 'none';
  }

  function updateNavVisibility() {
    const showNav = triggers.length > 1;
    if (prevBtn) prevBtn.hidden = !showNav;
    if (nextBtn) nextBtn.hidden = !showNav;
  }

  function showIndex(index) {
    currentIndex = (index + triggers.length) % triggers.length;
    render(readPayload(triggers[currentIndex]));
  }

  function openModal(index) {
    lastFocusedElement = document.activeElement;
    showIndex(index);
    updateNavVisibility();
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    modalImage.src = '';
    modalImage.alt = '';
    modalImage.style.display = 'none';
    modalVideo.innerHTML = '';
    modalVideo.setAttribute('aria-hidden', 'true');
    modalCaption.textContent = '';
    modalCaption.style.display = 'none';
    currentIndex = -1;
    if (lastFocusedElement) {
      lastFocusedElement.focus();
    }
  }

  triggers.forEach(function (trigger, index) {
    trigger.addEventListener('click', function () {
      openModal(index);
    });
  });

  if (prevBtn) {
    prevBtn.addEventListener('click', function () {
      showIndex(currentIndex - 1);
    });
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', function () {
      showIndex(currentIndex + 1);
    });
  }

  if (overlay) overlay.addEventListener('click', closeModal);
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (content) {
    content.addEventListener('click', function (e) {
      if (e.target === content) closeModal();
    });
  }

  document.addEventListener('keydown', function (e) {
    if (modal.getAttribute('aria-hidden') !== 'false') return;
    if (e.key === 'Escape') {
      closeModal();
    } else if (e.key === 'ArrowLeft' && triggers.length > 1) {
      showIndex(currentIndex - 1);
    } else if (e.key === 'ArrowRight' && triggers.length > 1) {
      showIndex(currentIndex + 1);
    }
  });

  // Trap focus in modal
  modal.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;

    const focusableElements = modal.querySelectorAll(
      'button:not([hidden]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else if (document.activeElement === lastFocusable) {
      firstFocusable.focus();
      e.preventDefault();
    }
  });
}
